import LogRocket from 'logrocket';
import { isLogRocketInitialized } from '@config/logRocketStatus';

/**
 * Reports failures to LogRocket.
 *
 * LogRocket only flags three things as errors on its own: uncaught exceptions,
 * `console.error`, and non-2xx responses. A failed GraphQL call is none of
 * them — the server answers HTTP 200 with an `errors` array, and every API hook
 * catches its own failure to raise a toast. So real failures left no trace in
 * the recorded session and there was nothing to filter on. This closes the gap
 * in one place, for every hook, including ones written later.
 *
 * Sprint 15 (story-073) adds one consistent custom event, `track('App Error', ...)`,
 * beside the existing `captureException` call at every report site, so "Custom Event:
 * App Error" becomes a single LogRocket session filter regardless of error class
 * (GraphQL, network, upload, render crash, cache). Event properties are restricted to
 * short enums/strings only — no variables, no response bodies, no PII.
 */

/**
 * Session expiry is an expected lifecycle event, not a defect — responseMiddleware
 * already logs the user out. Reporting it would bury real errors under noise.
 */
const IGNORED_CODES = ['UNAUTHENTICATED'];

/** `mutation CreateTask($input: ...)` -> `CreateTask`. */
const getOperationName = (query?: string): string =>
  query?.match(/\b(?:query|mutation|subscription)\s+(\w+)/)?.[1] ?? 'anonymous';

type GraphQLErrorLike = {
  message?: string;
  path?: ReadonlyArray<string | number>;
  extensions?: { code?: string };
};

export type ErrorReportSource = 'graphql' | 'network' | 'upload' | 'render' | 'cache';

/**
 * Reports a failure to LogRocket at both mechanisms this choke point maintains:
 * `captureException` (grouping, LogRocket's own dashboard) and `track('App Error', ...)`
 * (the single session filter). No-ops whenever LogRocket was never initialised, and
 * whenever the code is in `IGNORED_CODES`.
 */
const reportAppError = (
  errorName: string,
  message: string,
  source: ErrorReportSource,
  operation: string,
  code: string,
  extraTags: Record<string, string | number> = {},
  extra: Record<string, string | number> = {},
): void => {
  if (!isLogRocketInitialized()) return;
  if (IGNORED_CODES.includes(code)) return;

  // LogRocket groups exceptions by name + message, so building the key into
  // the message is what makes "every CreateTask failure" one filterable row
  // instead of hundreds of unrelated entries.
  const reported = new Error(message);
  reported.name = errorName;

  LogRocket.captureException(reported, {
    tags: { source, operation, code, ...extraTags },
    extra,
  });

  // Short enums/strings only — never variables, response bodies, emails, names, or ids.
  LogRocket.track('App Error', { source, operation, code });
};

/**
 * Failures already reported through `reportGraphQLErrors` (or, for the upload call
 * sites that report a raw response and then throw a *different* Error built from it,
 * explicitly via `markErrorReported`). `reportCacheError` — which react-query calls
 * for the very same failure moments after `responseMiddleware` or an upload path
 * already reported it — checks this set so it never reports the same failure twice.
 */
const reportedErrors = new WeakSet<object>();

const markReported = (value: unknown): void => {
  if (value && typeof value === 'object') reportedErrors.add(value);
};

/**
 * For the three upload call sites (`services/task/index.ts`, `services/fieldCapture/index.ts`)
 * that call `reportGraphQLErrors` on the raw `{ data, errors }` response and then throw a
 * brand-new `Error` built from it — a different object identity — so `reportCacheError`'s
 * WeakSet check on the thrown error would otherwise miss it. Call this on that new Error
 * right before throwing it.
 */
export const markErrorReported = markReported;

/**
 * Accepts whatever `graphql-request` hands its responseMiddleware: a thrown
 * ClientError (everything nested under `.response`) or a 200 response carrying
 * partial data alongside `errors`. When no GraphQL `errors` array is present, this
 * is a network/HTTP failure rather than a "nothing to report" case — it is now
 * reported as a single `source: 'network'` failure instead of being silently dropped.
 *
 * `options.source` lets the four hand-rolled upload paths (which parse the same
 * `{ data, errors }` response shape by hand) report through this same function while
 * staying filterable as `'upload'` rather than blending into ordinary GraphQL traffic.
 */
export const reportGraphQLErrors = (
  response: any,
  options?: { source?: 'graphql' | 'upload' },
): void => {
  if (!isLogRocketInitialized()) return;

  const source = options?.source ?? 'graphql';
  const errors: GraphQLErrorLike[] | undefined =
    response?.response?.errors ?? response?.errors;
  const operation = getOperationName(response?.request?.query);
  const status = response?.response?.status ?? response?.status;

  if (!errors?.length) {
    // `responseMiddleware` calls this on EVERY response, success or failure — a
    // successful response is a plain data object (no `.response` envelope, not an Error
    // instance), so it must stay silent here. A genuine network/HTTP failure with no
    // GraphQL error body is always either a thrown Error (offline, DNS, timeout — no
    // `.response` at all) or a `ClientError` whose `.response` envelope exists even when
    // the body itself carried no `errors` array (e.g. a non-JSON 500). The four hand-rolled
    // upload call sites construct one of these two failure shapes explicitly, so they are
    // covered by the same check without a separate flag.
    const isFailure = response instanceof Error || Boolean(response?.response);
    if (!isFailure) return;

    // This is the exact object `graphql-request` rethrows after its own catch handler
    // calls `responseMiddleware`, so marking it here is what lets `reportCacheError`
    // recognise the very same failure and skip it instead of reporting it twice.
    markReported(response);

    // A `ClientError`'s own `.message` is the raw JSON dump this sprint exists to stop
    // showing users — never forward it here either. Use the HTTP status when a `.response`
    // envelope exists; otherwise this is a plain thrown Error (e.g. "Failed to fetch"),
    // whose `.message` is just a sentence.
    const message = response?.response
      ? (typeof status === 'number' ? `HTTP ${status}` : 'Unknown response error')
      : (typeof response?.message === 'string' && response.message.trim()
          ? response.message
          : 'Unknown network error');

    reportAppError(
      'NetworkError',
      `[Network] ${operation}: ${message}`,
      source === 'upload' ? 'upload' : 'network',
      operation,
      'NETWORK_ERROR',
      typeof status === 'number' ? { status } : {},
    );
    return;
  }

  // Same identity rule as the network-failure branch above: mark the object so a
  // matching cache-handler failure is recognised as already reported.
  markReported(response);

  errors.forEach((error) => {
    const code = error?.extensions?.code ?? 'UNKNOWN';
    const message = error?.message ?? 'Unknown GraphQL error';

    reportAppError(
      'GraphQLError',
      `[GraphQL] ${operation}: ${message}`,
      source,
      operation,
      code,
      typeof status === 'number' ? { status } : {},
      { path: error?.path?.join('.') ?? '' },
    );
  });
};

/** Reports a render crash caught by `ErrorBoundary`. */
export const reportRenderCrash = (error: Error, componentStack?: string): void => {
  // The top frame of the stack names the crashing component, e.g. "in TaskCard".
  const operation = componentStack?.trim().split('\n')[0]?.trim().replace(/^in\s+/, '') || 'unknown';

  reportAppError('RenderCrash', `[Render] ${operation}: ${error.message}`, 'render', operation, 'RENDER_CRASH');
};

/**
 * True when `error` was already reported by `reportGraphQLErrors` (marked in
 * `reportedErrors`) or is self-evidently a GraphQL failure envelope (`.response` /
 * `.request`, the shape of a graphql-request `ClientError`) that `reportGraphQLErrors`
 * either already reported, or is about to via `responseMiddleware` on this same
 * failure. Either way `reportCacheError` must stay silent — it would otherwise
 * double-report every GraphQL failure react-query catches.
 */
const isAlreadyReported = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  if (reportedErrors.has(error as object)) return true;
  return 'response' in (error as Record<string, unknown>) || 'request' in (error as Record<string, unknown>);
};

/**
 * Reports a React Query cache (`QueryCache`/`MutationCache`) failure. Report only,
 * never a toast — and only for failures no other choke point already reported (see
 * `isAlreadyReported`). Never forwards a caught error's `.message`: for a `ClientError`
 * that would be the raw JSON dump this sprint exists to stop showing users, and for
 * anything else react-query hands us that could still be a stray path carrying request
 * variables. Uses a fixed description (the error's name, when it has one) instead.
 */
export const reportCacheError = (error: unknown, operation: string): void => {
  if (isAlreadyReported(error)) return;

  const description = error instanceof Error && error.name ? error.name : 'Query failed';

  reportAppError('CacheError', `[Cache] ${operation}: ${description}`, 'cache', operation, 'CACHE_ERROR');
};
